import React, { useEffect, useState } from 'react';
import { Tag, Avatar, Button, Input, Popover, Modal } from 'antd';
import {
  ArrowLeftOutlined,
  ShareAltOutlined,
  LikeOutlined,
  LikeFilled,
} from '@ant-design/icons';
import Comments from '../components/comment/Comments';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import UserAvatar from '../components/common/UserAvatar';
import friendService from '@/services/friendService';
import commentService from '@/services/commentService';
import { useParams } from 'react-router-dom';
import useAppMessage from '../hooks/useAppMessage';
import SharePopover from '../components/common/SharePopover';
import { SHARE_SCENE_TYPE, SHARE_QR_CONFIG } from '../utils/constants';
import { getCurrentUser } from '@/services/authService';
import { useEmbeddedAuth } from '../contexts/EmbeddedAuthContext';
import share from '../assets/share.svg';
// import deleteIcon from '../assets/del.svg';
import DeleteIcon from "../components/icons/Delete"
import ExpandAllIcon from "../components/icons/ExpandAllIcon"


const FindFriendDetailPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [commentsPopupVisible, setCommentsPopupVisible] = useState(true);
  const [comments, setComments] = useState([]);
  const [pagination, setPagination] = useState({
    offset: 0,
    total: 0,
    limit: 10,
  });
  const [loadingComments, setLoadingComments] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const { id } = useParams();
  const { message, contextHolder } = useAppMessage();
  const { showLoginModal } = useEmbeddedAuth();
  const [shareVisible, setShareVisible] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [loadingQr, setLoadingQr] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const currentUser = getCurrentUser();

  // 添加强制重新渲染的状态
  const [forceUpdate, setForceUpdate] = useState(0);
  // 正文折叠/展开
  const [contentExpanded, setContentExpanded] = useState(false);

  // 是否需要显示展开按钮
  const [showExpandButton, setShowExpandButton] = useState(false);
  const contentRef = React.useRef(null);
  const handleExpandAllClick = () => {
    if (!currentUser) {
      showLoginModal();
      return;
    }
    setContentExpanded(true);
  };

  useEffect(() => {
    if (contentRef.current) {
      // 512px is 32rem
      if (contentRef.current.scrollHeight > 512) {
        setShowExpandButton(true);
      } else {
        setShowExpandButton(false);
      }
    }
  }, [data]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);

    // 真实数据加载
    friendService
      .getRecruitPostDetail(id)
      .then((res) => {
        setData(res);
      })
      .catch((error) => {
      })
      .finally(() => setLoading(false));

    // 获取第一页评论
    setLoadingComments(true);
    commentService.getComments &&
      commentService
        .getComments(id, 1, 10)
        .then((res) => {
          // 适配新的评论数据结构
          setComments(res.comments || []);
          setPagination(
            res.metadata || { offset: 0, total: 0, limit: 10 }
          );
        })
        .finally(() => setLoadingComments(false));
  }, [id]);

  // 评论弹窗提交
  const handleCommentSubmit = (newComment) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        reviews: [newComment, ...(prev.reviews || [])],
      };
    });
  };

  // 加载更多评论
  const loadMoreComments = async () => {
    // 检查是否还有更多评论可以加载
    if (comments.length >= pagination.total) return;
    setLoadingComments(true);
    const nextOffset = pagination.offset + pagination.limit;
    try {
      const res = await commentService.getComments(
        id,
        nextOffset,
        pagination.limit
      );
      // 适配新的评论数据结构
      const newComments = res.comments || [];
      setComments((prev) => [...prev, ...newComments]);
      setPagination(res.metadata || pagination);
    } finally {
      setLoadingComments(false);
    }
  };

  // 刷新评论区
  const handleRefreshComments = async () => {
    setLoadingComments(true);
    try {
      const res = await commentService.getComments(id, 1, pagination.per_page);
      // 适配新的评论数据结构
      setComments(res.comments || []);
      setPagination(
        res.metadata || { offset: 0, total: 0, limit: 10 }
      );
      window.message?.success('评论发送成功');
    } catch (e) {
      window.message?.error('刷新评论失败');
    } finally {
      setLoadingComments(false);
    }
  };

  // 点赞
  const handleLike = async () => {
    if (!data || likeLoading) return;
    setLikeLoading(true);
    try {
      await friendService.toggleLike(data.id);
      setData((prev) => {
        if (!prev) return prev;
        const wasLiked = prev.is_liked || false;
        const currentLikeCount = prev.like_count || 0;
        const isLiked = !wasLiked;
        let newLikeCount = currentLikeCount;
        if (isLiked && !wasLiked) {
          newLikeCount = currentLikeCount + 1;
        } else if (!isLiked && wasLiked) {
          newLikeCount = Math.max(currentLikeCount - 1, 0);
        }
        return {
          ...prev,
          is_liked: isLiked,
          like_count: newLikeCount,
        };
      });
      window.message?.success(!data.is_liked ? '点赞成功' : '取消点赞');
    } catch (e) {
      window.message?.error('操作失败');
    } finally {
      setLikeLoading(false);
    }
  };

  // 收藏
  const handleFavorite = async () => {
    if (!data || favoriteLoading) return;
    setFavoriteLoading(true);
    try {
      await friendService.toggleFavorite(data.id);
      setData((prev) => {
        if (!prev) return prev;
        const wasFavorited = prev.is_favorited || false;
        const currentFavoriteCount = prev.favorite_count || 0;
        const isFavorited = !wasFavorited;
        let newFavoriteCount = currentFavoriteCount;
        if (isFavorited && !wasFavorited) {
          newFavoriteCount = currentFavoriteCount + 1;
        } else if (!isFavorited && wasFavorited) {
          newFavoriteCount = Math.max(currentFavoriteCount - 1, 0);
        }
        return {
          ...prev,
          is_favorited: isFavorited,
          favorite_count: newFavoriteCount,
        };
      });
      window.message?.success(!data.is_favorited ? '收藏成功' : '取消收藏');
    } catch (e) {
      window.message?.error('操作失败');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!data?.id) return;
    setDeleting(true);
    try {
      await friendService.deleteRecruitPost(data.id);
      window.message?.success('删除成功');
      setDeleteModalVisible(false);
      // 路由回退
      window.history.back();
    } catch (e) {
      window.message?.error('删除失败');
    } finally {
      setDeleting(false);
    }
  };

  // 附件/缩略图预览
  const renderAttachmentPreview = () => {
    // 注意：封面图改为在页面左上角小尺寸展示，这里不再单独大图展示

    // v2 API: 从fields.attachments获取附件UUID列表
    let attachmentUuids = (data?.fields?.attachments || []).slice();
    // 过滤掉封面图
    const coverImageUuid = data?.fields?.cover_image;
    if (coverImageUuid) {
      attachmentUuids = attachmentUuids.filter((u) => u !== coverImageUuid);
    }
    if (attachmentUuids.length > 0) {
      return (
        <div className="w-full">
          <div className="relative">
            {attachmentUuids.map((url, idx) => {        
              return (
                <div
                  key={url}
                  className="flex flex-col items-center justify-center mb-4"
                >
                  <div className="w-full h-full relative">
                    <img
                      src={url}
                      alt={`附件 ${idx + 1}`}
                      className="w-full h-full object-cover rounded-lg shadow-lg"
                      onClick={() => window.open(url, '_blank')}
                      style={{ cursor: 'pointer' }}
                      onError={(e) => {
                        // 如果图片加载失败，可能是视频或其他文件，显示通用图标
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        if (!parent.querySelector('.file-placeholder')) {
                          const placeholder = document.createElement('div');
                          placeholder.className = 'file-placeholder w-full h-32 flex items-center justify-center bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors';
                          placeholder.innerHTML = '<span class="text-gray-600 font-medium">📁 文件附件</span>';
                          placeholder.onclick = () => window.open(url, '_blank');
                          parent.appendChild(placeholder);
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
            {/* Paper airplane icon */}
            <div className="absolute top-4 right-4">
              <ShareAltOutlined className="text-white text-2xl opacity-70" />
            </div>
          </div>
        </div>
      );
    }

    // v1兼容：展示 attachments
    if (data?.attachments && data.attachments.length > 0) {
      return (
        <div className="w-full">
          <div className="relative">
            {data.attachments.map((att, idx) => {
              const contentType = att.content_type || '';
              // v1模式下，如果thumb存在则视为封面，附件列表不重复展示
              if (data?.thumb?.url && att.url === data.thumb.url) return null;
              if (contentType.startsWith('image/')) {
                return (
                  <div
                    key={idx}
                    className="flex flex-col items-center justify-center mb-4"
                  >
                    <div className="w-full h-full relative">
                      <img
                        src={att.url}
                        alt={att.name || att.id}
                        className="w-full h-full object-cover rounded-lg shadow-lg"
                        onClick={() => window.open(att.url, '_blank')}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                );
              }
              if (contentType.startsWith('video/')) {
                return (
                  <div
                    key={idx}
                    className="flex flex-col items-center justify-center mb-4"
                  >
                    <video
                      src={att.url}
                      className="w-full h-full rounded-lg shadow-lg object-cover"
                      controls
                    />
                  </div>
                );
              }
              // 其他类型
              return (
                <div
                  key={idx}
                  className="w-full h-full flex flex-col items-center justify-center font-bold text-xl cursor-pointer hover:shadow-xl transition-shadow mb-4"
                  onClick={() => window.open(att.url, '_blank')}
                  title={att.name}
                >
                  {att.content_type || '文件'}
                </div>
              );
            })}
            {/* Paper airplane icon */}
            <div className="absolute top-4 right-4">
              <ShareAltOutlined className="text-white text-2xl opacity-70" />
            </div>
          </div>
        </div>
      );
    }

    // 没有任何附件或图片
    return null;
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[var(--color-bg-layout)] flex items-center justify-center">
        <div className="text-gray-500">加载中...</div>
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen bg-[var(--color-bg-layout)] flex items-center justify-center">
        <div className="text-gray-500">未找到数据</div>
      </div>
    );

  // 临时简化版本，测试页面是否能正常渲染
  return (
    <div className="min-h-screen bg-[var(--color-bg-layout)]">
      {contextHolder}
      <div className="max-w-4xl mx-auto py-6 px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
          >
            <ArrowLeftOutlined />
            <span className="text-sm">首页</span>
          </button>
        </div>

        {/* Title / Cover / Actions */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-start gap-4 flex-1 mr-8">
            {/* 小封面图（左上角） */}
            {(() => {
              const coverUrl = data?.fields?.cover_image || data?.thumb?.url || null;
              if (!coverUrl) return null;
              return (
                <img
                  src={coverUrl}
                  alt="封面图片"
                  className="w-20 h-20 object-cover rounded-lg shadow"
                  onClick={() => window.open(coverUrl, '_blank')}
                  style={{ cursor: 'pointer' }}
                />
              );
            })()}
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-white mb-4 leading-tight">
                {data?.prompt || data?.fields?.title || ''}
              </h1>
              {/* 作者与标签 */}
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <UserAvatar
                    size={32}
                    className="bg-[var(--color-primary-bg)] text-[var(--color-primary)]"
                    src={data?.user?.avatar}
                    name={data?.user?.name}
                    onClick={() => {
                      if (!data?.user?.id) return;
                      if (!currentUser) {
                        showLoginModal();
                        return;
                      }
                      window.location.assign(`/personal-info/${data.user.id}`);
                    }}
                    style={{ cursor: data?.user?.id ? 'pointer' : 'default' }}
                  />
                  <span className="text-white">来自</span>
                  <span
                    className="font-medium text-white"
                    style={{ cursor: data?.user?.id ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (!data?.user?.id) return;
                      if (!currentUser) {
                        showLoginModal();
                        return;
                      }
                      window.location.assign(`/personal-info/${data.user.id}`);
                    }}
                  >
                    {data?.user?.name}
                  </span>
                </div>
                <div className="flex gap-2">
                  {(data?.topics || data?.fields?.tags || []).map((tag) => (
                    <Tag
                      key={tag}
                      className="bg-[#0FC5BB] text-black rounded px-[10.5px] h-4 leading-4 text-xs"
                      style={{ letterSpacing: '0.01em' }}
                    >
                      {tag}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SharePopover
              message={message}
              userInfo={data}
              basicUserInfo={data?.user}
              qrConfig={SHARE_QR_CONFIG[SHARE_SCENE_TYPE.FRIEND]}
            >
              <button className="flex flex-col items-center gap-1 px-2 py-1 text-white border-none rounded-lg cursor-pointer text-sm">
                <img
                  src={share}
                  alt="share"
                  className="w-5 h-5"
                  style={{ filter: 'var(--color-primary-filter)' }}
                />
                分享
              </button>
            </SharePopover>
            {data?.user?.id &&
              currentUser?.id &&
              data.user.id === currentUser.id && (
                // <Button
                //   danger
                //   icon={<DeleteOutlined />}
                  // loading={deleting}
                  // onClick={() => setDeleteModalVisible(true)}
                  // className="flex items-center gap-2 px-4 py-2 border-none rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
                // >
                //   删除
                // </Button>
                <button 
                className="flex flex-col items-center gap-1 px-2 py-1 text-gray-600 hover:text-red-600 border-none rounded-lg cursor-pointer bg-transparent text-sm" 
                onClick={() => setDeleteModalVisible(true)}
              >
              <DeleteIcon className="w-5 h-5" color="currentColor"/>
                删除
              </button>
              )}
            <Modal
              open={deleteModalVisible}
              title="确认删除"
              onOk={handleDelete}
              onCancel={() => setDeleteModalVisible(false)}
              okText="确认"
              cancelText="取消"
              confirmLoading={deleting}
              maskClosable={false}
            >
              <p>确定要删除该帖子吗？此操作不可撤销。</p>
            </Modal>
          </div>
        </div>

        {/* 作者与标签已移动到标题下方 */}

        {/* Main Content */}
        <div className="bg-[var(--color-bg-container)] rounded-2xl shadow-sm overflow-hidden">
          

          {/* Content */}
          <div className="px-6 pb-6">
            <div className="prose prose-invert max-w-none relative">
              <div
                ref={contentRef}
                data-testid="post-content"
                className={`${contentExpanded ? '' : 'max-h-[32rem] overflow-hidden'}`}
              >
                <MarkdownRenderer className="text-[14px]" content={data?.response || data?.fields?.body_text || '暂无内容'} textColor="text-white" />
              </div>
              {!contentExpanded && showExpandButton && (
                <>
                  <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-[var(--color-bg-container)] to-transparent z-0" />
                  <div className="flex justify-center -mt-3 relative z-10 w-full">
                    <button
                      className="w-8 h-8 bg-[#333333] rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                      onClick={handleExpandAllClick}
                      aria-label="展开所有"
                    >
                      <ExpandAllIcon />
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--color-border-secondary)]">
              <div className="flex items-center gap-6">
                <button
                  className={`flex items-center gap-2 transition-colors ${
                    data?.is_liked
                      ? 'text-red-500'
                      : 'text-white hover:text-red-500'
                  }`}
                  onClick={handleLike}
                  disabled={likeLoading}
                >
                  {data?.is_liked ? (
                    <LikeFilled className="text-lg" />
                  ) : (
                    <LikeOutlined className="text-lg" />
                  )}
                  <span className="text-sm font-medium">
                    {data?.like_count || 0}
                  </span>
                </button>
                {/* <button
                  className={`flex items-center gap-2 transition-colors ${
                    data?.is_favorited
                      ? 'text-yellow-500'
                      : 'text-gray-600 hover:text-yellow-500'
                  }`}
                  onClick={handleFavorite}
                  disabled={favoriteLoading}
                >
                  {data?.is_favorited ? (
                    <StarFilled className="text-lg" />
                  ) : (
                    <StarOutlined className="text-lg" />
                  )}
                  <span className="text-sm font-medium">
                    {data?.favorite_count || 0}
                  </span>
                </button> */}
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="px-6 pb-6">
            <Comments contentId={data?.id} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindFriendDetailPage;
